import React, { useMemo } from 'react';
import { useSWRConfig } from 'swr';
import { Info, User } from 'lucide-react';
import { toast } from 'sonner';
import EmailCandidateForm from './EmailCandidateForm';
import UIStrings from '@/Utils/UIStrings';
import BlueRibbonDialog from '../../components/BlueRibbonDialog';
import { useCachedProfileContactInfos } from '../../../../api/useProfileContactInfo';
import {
    getOutreachEmailThreadUrl,
    useOutreachEmailTemplate,
    useSendOutreachEmail,
} from '../../../../api/useOutreachCandidate';
import useRecruitmentId from '@/Hooks/useRecruitmentId';
import { useShortlistedListContext } from '../../utils/ShortlistedListContext';
import { useOutreachCandidatePopoutContext } from '../OutreachContext';
import { useShortlistedTableContext } from '../../utils/ShortlistedTableContext';
import { cn } from '@/lib/utils';

const EmailCandidateFormContainer = () => {
    const { closeOutreachPopout, selectedProfile } = useOutreachCandidatePopoutContext();
    const { resetRowSelection } = useShortlistedTableContext();
    const isSingleProfile = selectedProfile.length === 1;
    const contactInfos = useCachedProfileContactInfos(selectedProfile.map(({ url }) => url));
    const validContacts = useMemo(
        () =>
            selectedProfile.filter((profile) => {
                const contact = contactInfos.find(({ url }) => url === profile.url);
                return contact?.info?.personalEmailAvailable && !profile.emailSentCount;
            }),
        [selectedProfile, contactInfos],
    );
    const invalidContactsCount = selectedProfile.length - validContacts.length;
    const { cache } = useSWRConfig();
    const { trigger: sendEmail } = useSendOutreachEmail();
    const { mutate: mutateShortlistedProfiles } = useShortlistedListContext();
    const { mutate: mutateEmailTemplate } = useOutreachEmailTemplate();
    const recruitmentId = useRecruitmentId();

    const mutateEmailThreads = () => {
        validContacts.forEach(({ id }) => cache.delete(getOutreachEmailThreadUrl(recruitmentId, id)));
    };

    return (
        <div
            className={`grid max-h-[inherit] overflow-auto ${!isSingleProfile ? 'grid-cols-1 md:grid-cols-[minmax(260px,35%)_1fr]' : 'grid-cols-1'}`}
        >
            {!isSingleProfile && (
                <div className="bg-muted border-border flex min-h-0 flex-col border-r">
                    <div className="pl-6">
                        <BlueRibbonDialog className="w-full max-w-none rounded-tl-none rounded-tr-none rounded-br-none rounded-bl-md text-center">
                            {UIStrings.EmailOutreachMultipleCandidates}
                        </BlueRibbonDialog>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
                        <h2 className="text-foreground text-xl font-semibold">
                            {UIStrings.SendEmailToTheFollowingCandidates}
                        </h2>
                        <div className="border-border bg-background max-h-[491px] flex-1 overflow-auto rounded-md border p-3">
                            <ul className="flex flex-col gap-3">
                                {validContacts.map(({ id, name }) => (
                                    <li key={id} className="text-secondary-foreground flex items-center gap-2">
                                        <User className="shrink-0" />
                                        <span className="text-base font-medium">{name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {!!invalidContactsCount && (
                            <div className="flex rounded-md p-2 transition-colors duration-200">
                                <div className="text-foreground flex items-end gap-2">
                                    <Info className="shrink-0" />
                                    <p className="text-sm font-bold">
                                        {UIStrings.AmountOfCandidateWasSkipped(invalidContactsCount)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="flex min-h-0 min-w-0 flex-col">
                <div className="flex items-start justify-between pr-2">
                    {isSingleProfile && (
                        <div className="pl-6">
                            <BlueRibbonDialog>{UIStrings.EmailOutreach}</BlueRibbonDialog>
                        </div>
                    )}
                </div>
                <div className={cn('min-h-0 flex-1 overflow-auto px-6 pt-6 pb-6', !isSingleProfile && 'pt-12')}>
                    <EmailCandidateForm
                        onSubmit={async ({ emailHtml, subject }) => {
                            await sendEmail({
                                subject,
                                htmlBody: emailHtml,
                                shortlistedProfilesIds: validContacts.map(({ id }) => id),
                            });

                            mutateShortlistedProfiles();
                            mutateEmailTemplate();
                            mutateEmailThreads();

                            resetRowSelection();
                            closeOutreachPopout();
                            toast.success(UIStrings.EmailsSentSuccessfully);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default EmailCandidateFormContainer;
